import {
    _decorator,
    Node,
    Camera,
    Color,
    SpriteFrame,
    RenderTexture,
    Sprite,
    Layers,
    Texture2D,
    warn,
    view,
    screen,
    find,
    TransformBit,
    Mask,
    director,
    Director,
    Vec3,
} from 'cc';
import { DEV } from 'cc/env';
import GameDependency from '../../../../System/GameDependency';
import BindNode from '../../Misc/BindNode';

const { ccclass, property } = _decorator;
const nodeLayerMap: Map<Node, number> = new Map();
const nodeMarkFlagMap: Map<Node, number> = new Map();
const POST_PROCESSING_SNAPSHOT_NODE_NAME = 'PostProcessing_Snapshot_Node';

interface IPostProcessingEffectInfo {
    name: string;
    type: string;
}

interface IPostProcessingEffect {
    effectNode: Node;
    enabled: boolean;
    visibility: number;
    updateLayer(layer: number, preserveLastLayer: boolean): void;
}

class PostProcessingEffect implements IPostProcessingEffect, IPostProcessingEffectInfo {
    private _sprite: Sprite = null;
    private _renderTexture: RenderTexture = null;
    private _camera: Camera = null;
    private _effectType: string = 'Unknown';

    constructor(
        private _index: number,
        private readonly _effectNode: Node,
        private readonly _getCamera: (index: number) => Camera,
        private readonly _postProcessing: PostProcessing,
    ) {
        // initialize render texture
        const { width, height } = screen.resolution;
        this._renderTexture = new RenderTexture();
        this._renderTexture.initialize({ width, height });
        this._renderTexture.setWrapMode(Texture2D.WrapMode.CLAMP_TO_EDGE, Texture2D.WrapMode.CLAMP_TO_EDGE);

        // initialize sprite
        this._sprite = _effectNode.getComponent(Sprite) ?? _effectNode.addComponent(Sprite);
        this._sprite.sizeMode = Sprite.SizeMode.CUSTOM;
        const spriteFrame = new SpriteFrame();
        spriteFrame.texture = this._renderTexture;
        spriteFrame.flipUVY = true;
        spriteFrame.packable = false;
        this._sprite.spriteFrame = spriteFrame;

        // check if the effect node has a custom material and extract effect type
        if (!this._sprite.customMaterial) {
            if (this._effectNode.name !== POST_PROCESSING_SNAPSHOT_NODE_NAME) {
                warn(`No effect material found on node: ${this._effectNode.name}.`);
            }
        } else {
            const effectAssetName = this._sprite.customMaterial.effectAsset.name;
            this._effectType = effectAssetName.split('/').pop();
        }

        // initialize camera
        this._attachCamera();
        this._effectNode.on(Node.EventType.ACTIVE_CHANGED, this._onEffectNodeActiveChanged, this);

        // keep position synced
        const bindTarget = this._postProcessing.fullscreen ? find('Canvas') : this._postProcessing.node;
        let bindNode = this._effectNode.getComponent(BindNode);
        if (!bindNode) {
            bindNode = this._effectNode.addComponent(BindNode);
        }
        bindNode.target = bindTarget;
        bindNode.bindFlag = TransformBit.POSITION;

        // keep size synced
        if (this._postProcessing.fullscreen) {
            view.on('canvas-resize', this._updateSpriteSize, this);
            view.on('render-window-resize', this._updateTextureSize, this);
        } else {
            this._postProcessing.on(Node.EventType.SIZE_CHANGED, this._updateSpriteSize, this);
            this._postProcessing.on(Node.EventType.SIZE_CHANGED, this._updateTextureSize, this);
        }
        this._updateSpriteSize();
        this._updateTextureSize();
    }

    public get name() {
        return this._effectNode.name;
    }

    public get type() {
        return this._effectType;
    }

    public get index() {
        return this._index;
    }
    public set index(value: number) {
        if (this._index === value) {
            return;
        }
        this._index = value;
        this._attachCamera();
    }

    public get enabled() {
        return this._effectNode.active;
    }
    public set enabled(value: boolean) {
        this._effectNode.active = value;
    }

    public get visibility() {
        return this._camera.visibility;
    }

    public get effectNode() {
        return this._effectNode;
    }

    public get renderTexture() {
        return this._renderTexture;
    }

    public updateLayer(layer: number, preserveLastLayer: boolean) {
        const newLayer = preserveLastLayer ? this._effectNode.layer | layer : layer;
        if (this._effectNode.layer === newLayer) {
            return;
        }
        this._effectNode.layer = newLayer;
    }

    public destroy() {
        if (this._postProcessing.fullscreen) {
            view.off('canvas-resize', this._updateSpriteSize, this);
            view.off('render-window-resize', this._updateTextureSize, this);
        } else {
            this._postProcessing.off(Node.EventType.SIZE_CHANGED, this._updateSpriteSize, this);
            this._postProcessing.off(Node.EventType.SIZE_CHANGED, this._updateTextureSize, this);
        }
        if (this.effectNode.name !== POST_PROCESSING_SNAPSHOT_NODE_NAME) {
            this._renderTexture.destroy();
        }
        if (this._effectNode.isValid) {
            this._effectNode.off(Node.EventType.ACTIVE_CHANGED, this._onEffectNodeActiveChanged, this);
            this._effectNode.destroy();
        }
        if (this._camera.isValid) {
            this._camera?.node?.destroy();
        }
    }

    private _attachCamera() {
        // change used camera and update properties
        this._camera = this._getCamera(this.index);
        this._camera.node.active = this.enabled;
        this._camera.targetTexture = this._renderTexture;
        this._effectNode.layer = Layers.Enum.NONE;
    }

    private _onEffectNodeActiveChanged() {
        this._camera.node.active = this.enabled;
    }

    private _updateSpriteSize() {
        if (this._postProcessing.fullscreen) {
            // sprite need to cover the entire window size and divide window size by view scale to get the correct value in design resolution space
            this._sprite.node.transform.setContentSize(screen.windowSize.width / view.getScaleX(), screen.windowSize.height / view.getScaleY());
        } else {
            const { transformWidth, transformHeight } = this._postProcessing.node;
            this._sprite.node.transform.setContentSize(transformWidth, transformHeight);
        }
    }

    private _updateTextureSize() {
        if (this._postProcessing.fullscreen) {
            // resize render texture and camera to match the new resolution
            this._renderTexture.resize(
                screen.resolution.width * this._postProcessing.textureRatio,
                screen.resolution.height * this._postProcessing.textureRatio,
            );
        } else {
            const { transformWidth, transformHeight } = this._postProcessing.node;
            this._renderTexture.resize(transformWidth * this._postProcessing.textureRatio, transformHeight * this._postProcessing.textureRatio);
        }
    }
}

@ccclass('PostProcessingSnapshotConfig')
class PostProcessingSnapshotConfig {
    @property({
        tooltip: DEV && '是否進行截圖',
    })
    public readonly enable: boolean = false;

    @property({
        tooltip: DEV && '是否在 onLoad 時就進行截圖',
        visible(this: PostProcessingSnapshotConfig) {
            return this.enable;
        },
    })
    public readonly snapshotOnLoad: boolean = true;

    @property({
        tooltip: DEV && 'render texture 截圖的 key',
        visible(this: PostProcessingSnapshotConfig) {
            return this.enable;
        },
    })
    public readonly snapshotKey: string = '';

    @property({
        tooltip: DEV && '快照的優先順序，越大越晚進行快照',
        visible(this: PostProcessingSnapshotConfig) {
            return this.enable;
        },
    })
    public readonly priority: number = 0;

    @property({
        tooltip: DEV && '截圖後是否銷毀 Camera 和 RenderTexture 以節省資源 (不可重新截圖)',
        visible(this: PostProcessingSnapshotConfig) {
            return this.enable;
        },
    })
    public readonly destroyAfterSnapshot: boolean = true;

    public isQueued: boolean = false;
}

@ccclass('PostProcessing')
export default class PostProcessing extends GameDependency {
    private static readonly INSTANCES: PostProcessing[] = [];
    private static readonly USED_LAYERS: number[] = [];
    private static readonly FINAL_TARGET: IPostProcessingEffect = {
        effectNode: null,
        enabled: true,
        visibility: Layers.BitMask.UI_2D,
        updateLayer: () => {
            throw new Error('Should not call updateLayer on final effect.');
        },
    };
    private static _currentMarkFlag: number = -1;
    private static _effectDirty = false;
    private static _maskLayer: number = Layers.Enum.UI_2D;

    @property({
        tooltip: DEV && '是否全屏後效，若為 false，則使用 PostProcessing 節點本身的大小作為後效照射範圍',
    })
    public readonly fullscreen: boolean = true;

    @property({
        tooltip: DEV && '後效作用節點是否仍由 MainCamera 繪製原圖，後效結果會疊在原圖之上，讓後效範圍外的內容仍能正常顯示',
    })
    public readonly keepOriginalRender: boolean = false;

    @property({
        max: 1,
        min: 0,
        tooltip: DEV && '後效結果的貼圖解析度比例',
    })
    public readonly textureRatio: number = 1.0;

    @property({
        type: [Node],
        tooltip: DEV && '排除指定的根節點',
    })
    public readonly excludeProcessingNodes: Node[] = [];

    @property({
        type: [Node],
        tooltip: DEV && '後效作用節點',
    })
    public readonly needPostProcessingNodes: Node[] = [];

    @property({
        type: [Node],
        tooltip: DEV && '後效節點，若節點設為 Inactive，節點上的特效將不會被啟用',
    })
    private readonly effectPasses: Node[] = [];

    @property
    private _priority: number = 0;

    @property
    private _snapshotConfig: PostProcessingSnapshotConfig = new PostProcessingSnapshotConfig();

    private readonly _effects: PostProcessingEffect[] = [];
    private readonly _effectApplySequence: PostProcessingEffect[] = [];
    private readonly _cameras: Camera[] = [];
    private _snapshotEffectNode: Node = null;

    @property({
        tooltip: DEV && '後效優先順序，數字越大越晚渲染',
        visible(this: PostProcessing) {
            return !this._snapshotConfig.enable;
        },
    })
    public get priority(): number {
        return this._priority;
    }
    public set priority(value: number) {
        if (this._priority === value) {
            return;
        }
        this._priority = value;
        this._applyEffect();
    }

    @property(PostProcessingSnapshotConfig)
    public get snapshotConfig() {
        return this._snapshotConfig;
    }
    public set snapshotConfig(value: PostProcessingSnapshotConfig) {
        if (this._snapshotConfig === value) {
            return;
        }
        this._snapshotConfig = value;
    }

    public get requiredLayerCount() {
        // effect camera + snapshot camera(if enabled)
        return this.effectPasses.length + +this._snapshotConfig.enable;
    }

    public get enabledEffectCount() {
        return this._effects.filter((effect) => effect.enabled).length;
    }

    private static _applyEffects() {
        PostProcessing._effectDirty = true;
    }

    private static _doApplyEffects() {
        if (!PostProcessing._effectDirty) {
            return;
        }
        PostProcessing._effectDirty = false;
        PostProcessing._currentMarkFlag++;
        const instancesInOrder = [...PostProcessing.INSTANCES].sort((a, b) => a.priority - b.priority);
        let lastEffect: IPostProcessingEffect = null;
        instancesInOrder.forEach((postProcessing, idx) => {
            const isLastInstance = idx === instancesInOrder.length - 1;
            lastEffect = PostProcessing._applyEffect(postProcessing, lastEffect, isLastInstance);
        });
    }

    private static _applyEffect(postProcessing: PostProcessing, lastEffect: IPostProcessingEffect, outputToScreen: boolean) {
        const effectSequence: IPostProcessingEffect[] = [...postProcessing._effectApplySequence];
        const isTakingSnapshot = postProcessing._snapshotConfig.enable && postProcessing.enabledEffectCount > 0;
        if (outputToScreen) {
            effectSequence.push(PostProcessing.FINAL_TARGET);
        }
        let firstEffectOfInstance = true;
        for (let i = 0; i < effectSequence.length; i++) {
            const effect = effectSequence[i];
            if (!effect.enabled) {
                continue;
            }
            // 設定當前特效作用節點的 layer，使其對特效可見
            if (firstEffectOfInstance) {
                // 保留 UI_2D，讓 MainCamera 仍繪製原圖
                const targetLayer = postProcessing.keepOriginalRender ? effect.visibility | Layers.Enum.UI_2D : effect.visibility;
                postProcessing.needPostProcessingNodes.forEach((node) => {
                    postProcessing._setNodeLayerRecursively(node, targetLayer, isTakingSnapshot);
                    // 確保父節點的 Mask 可見
                    postProcessing._ensureAscendantMaskIsVisible(node);
                });
                firstEffectOfInstance = false;
            }
            // 設定後效節點的 layer ，使其對下個後效可見
            if (lastEffect) {
                lastEffect.updateLayer(effect.visibility, isTakingSnapshot);
                // 確保父節點的 Mask 可見
                postProcessing._ensureAscendantMaskIsVisible(lastEffect.effectNode);
            }
            lastEffect = effect;
        }
        return lastEffect;
    }

    public onLoad() {
        if (!this._snapshotConfig.enable) {
            PostProcessing.INSTANCES.push(this);
        }
        this._init();
        if (!director.hasEventListener(Director.EVENT_BEFORE_DRAW, PostProcessing._doApplyEffects, PostProcessing)) {
            director.on(Director.EVENT_BEFORE_DRAW, PostProcessing._doApplyEffects, PostProcessing);
        }
    }

    public onDestroy() {
        const index = PostProcessing.INSTANCES.indexOf(this);
        if (index !== -1) {
            PostProcessing.INSTANCES.splice(index, 1);
        }
        if (PostProcessing.INSTANCES.length) {
            PostProcessing._applyEffects();
        } else {
            PostProcessing._currentMarkFlag++;
            this.needPostProcessingNodes.forEach((node) => {
                this._setNodeLayerRecursively(node, Layers.Enum.UI_2D);
            });
        }
        this.effectPasses.forEach((effectPass) => {
            if (effectPass.isValid) {
                effectPass.off(Node.EventType.ACTIVE_IN_HIERARCHY_CHANGED, this._applyEffect, this);
            }
        });
        this._effects.forEach((effect) => {
            Layers.freeLayer(effect.visibility);
            PostProcessing.USED_LAYERS.splice(PostProcessing.USED_LAYERS.indexOf(effect.visibility), 1);
            effect.destroy();
        });
        view.off('canvas-resize', this._onCanvasResize, this);
    }

    public getEffectInfo(effectIndex: number): IPostProcessingEffectInfo {
        if (effectIndex < 0 || effectIndex >= this._effects.length) {
            warn(`Invalid effect index: ${effectIndex}.`);
            return null;
        }
        const effect = this._effects[effectIndex];
        return { name: effect.name, type: effect.type };
    }

    /**
     * @param applyOrder 特效套用順序列表
     * @example [2,0,1] 將會把 [a,b,c] 變成 [b,c,a]
     */
    public updateEffectApplyOrder(applyOrder: number[]) {
        if (this.effectPasses.length !== applyOrder.length) {
            warn(`Invalid applyOrder length: ${applyOrder.length}, expected ${this.effectPasses.length}.`);
            return;
        }
        const missingOrderIndex = this.effectPasses.findIndex((e, idx) => !applyOrder.includes(idx));
        if (missingOrderIndex !== -1) {
            warn(`missing apply order for effect index: ${missingOrderIndex}.`);
            return;
        }

        for (let i = 0; i < applyOrder.length; i++) {
            const effect = this._effects[i];
            const newOrder = applyOrder[i];
            effect.index = newOrder;
            this._effectApplySequence[newOrder] = effect;
        }
        this._applyEffect();
    }

    public enableEffect(effectIndex: number, enabled: boolean) {
        if (effectIndex < 0 || effectIndex >= this._effects.length) {
            warn(`Invalid effect index: ${effectIndex}.`);
            return;
        }
        const effect = this._effects[effectIndex];
        effect.enabled = enabled;
        this._applyEffect();
    }

    public snapshot() {
        this._createSnapshot();
        this._applyEffect();
    }

    private _init() {
        view.on('canvas-resize', this._onCanvasResize, this);
        for (let i = 0; i < this.effectPasses.length; i++) {
            const effectPass = this.effectPasses[i];
            this._effects[i] = new PostProcessingEffect(i, effectPass, (cameraIndex: number) => this._getCamera(cameraIndex), this);
            effectPass.on(Node.EventType.ACTIVE_IN_HIERARCHY_CHANGED, this._applyEffect, this);
        }
        this._effectApplySequence.push(...this._effects);
        if (this._snapshotConfig.enable) {
            if (!this._snapshotConfig.isQueued && this._snapshotConfig.snapshotOnLoad) {
                this._snapshotConfig.isQueued = true;
                this.game.snapshot.queue({ postProcessing: this, priority: this._snapshotConfig.priority });
            }
        } else {
            this._applyEffect();
        }
    }

    private _onCanvasResize() {
        // divide window size by view scale to get the correct value in design resolution space
        this._cameras.forEach((camera) => {
            camera.orthoHeight = (this.fullscreen ? screen.windowSize.height / view.getScaleY() : this.node.transformHeight) / 2;
        });
    }

    private _applyEffect() {
        // decide apply type
        const isSnapshot = this._snapshotConfig.enable;
        if (isSnapshot) {
            PostProcessing._currentMarkFlag++;
            // output to screen only when no effect is enabled (need to reset the node layer back to UI_2D)
            PostProcessing._applyEffect(this, null, this.enabledEffectCount === 0);
        } else {
            PostProcessing._applyEffects();
        }
    }

    private _ensureAscendantMaskIsVisible(node: Node) {
        if (!node || !node.isValid) {
            return;
        }
        const mask = node.getComponent(Mask);
        if (mask) {
            const newLayer = node.layer | PostProcessing._maskLayer;
            if (node.layer !== newLayer) {
                node.layer = newLayer;
            }
        }
        if (node.parent instanceof Node) {
            this._ensureAscendantMaskIsVisible(node.parent);
        }
    }

    /**
     * 遞迴設定 node 與其子節點的 layer
     * @param node 根節點
     * @param layer 目標 layer，預設為父節點 layer，若無父節點則使用 UI_2D
     */
    private _setNodeLayerRecursively(node: Node, layer?: number, preserveLastLayer: boolean = false) {
        if (this.excludeProcessingNodes.includes(node)) {
            return;
        }
        if (nodeMarkFlagMap.get(node) === PostProcessing._currentMarkFlag) {
            return;
        }
        nodeMarkFlagMap.set(node, PostProcessing._currentMarkFlag);
        if (layer && preserveLastLayer) {
            layer |= node.layer;
        }
        const targetLayer = layer ?? (node.parent ? nodeLayerMap.get(node.parent) ?? Layers.Enum.UI_2D : Layers.Enum.UI_2D);
        nodeLayerMap.set(node, targetLayer);
        // only change ui-2d layer or used layers
        if (node.layer !== targetLayer && (node.layer === Layers.Enum.UI_2D || PostProcessing.USED_LAYERS.some((usedLayer) => usedLayer & node.layer))) {
            node.layer = targetLayer;
        }
        if (!node.hasEventListener(Node.EventType.CHILD_ADDED, this._onChildAdded, this)) {
            node.on(Node.EventType.CHILD_ADDED, this._onChildAdded, this);
        }
        node.children.forEach((subChild) => this._setNodeLayerRecursively(subChild, layer));
    }

    private _onChildAdded(child: Node) {
        // 開新的標記輪次，避免重新掛載的節點因已標記而被略過；不傳 layer 以繼承父節點的 layer
        PostProcessing._currentMarkFlag++;
        this._setNodeLayerRecursively(child);
    }

    private _getCamera(index: number): Camera {
        if (index < 0) {
            warn(`Invalid camera index: ${index}.`);
            return null;
        }

        if (this._cameras[index]) {
            return this._cameras[index];
        }
        // generate camera
        let cameraNode = this.node.getChildByName(`Camera${index}`);
        if (!cameraNode) {
            cameraNode = new Node(`Camera${index}`);
            cameraNode.parent = this.node;
        }
        let camera = cameraNode.getComponent(Camera);
        if (!camera) {
            const layer = Layers.getUnusedUserLayer();
            if (layer === Layers.Enum.NONE) {
                warn('No unused layer available for PostProcessing camera.');
                return null;
            }
            PostProcessing.USED_LAYERS.push(layer);
            PostProcessing._maskLayer |= layer;
            camera = cameraNode.addComponent(Camera);
            camera.priority = index;
            camera.visibility = layer;
            camera.clearFlags = Camera.ClearFlag.SOLID_COLOR;
            camera.clearColor = Color.TRANSPARENT;
            camera.projection = Camera.ProjectionType.ORTHO;
            camera.near = 0;
            camera.far = 2000;
            camera.orthoHeight = (this.fullscreen ? screen.windowSize.height / view.getScaleY() : this.node.transformHeight) / 2;
            camera.node.setPosition(0, 0, camera.far / 2);
        }
        this._cameras[index] = camera;
        return camera;
    }

    private _createSnapshot() {
        const getLastApplyEffect = () => this._effectApplySequence[this._effectApplySequence.length - 1];

        // Add an empty post-processing effect to capture the final render texture
        if (!this._snapshotEffectNode) {
            const effectNode = new Node(POST_PROCESSING_SNAPSHOT_NODE_NAME);
            const lastApplyEffect = getLastApplyEffect().effectNode;
            effectNode.parent = lastApplyEffect.parent;
            effectNode.setSiblingIndex(lastApplyEffect.getSiblingIndex() + 1);
            effectNode.setWorldScale(Vec3.ONE);
            const emptyPostProcessingEffect = new PostProcessingEffect(
                this._effects.length,
                effectNode,
                (cameraIndex: number) => this._getCamera(cameraIndex),
                this,
            );
            effectNode.on(Node.EventType.ACTIVE_IN_HIERARCHY_CHANGED, this._applyEffect, this);
            this.effectPasses.push(effectNode);
            this._effects.push(emptyPostProcessingEffect);
            this._effectApplySequence.push(emptyPostProcessingEffect);
            this._snapshotEffectNode = effectNode;
        }

        // Apply all effects to make sure the final effect is in the correct state before snapshot
        this._effects.forEach((effect) => {
            effect.enabled = true;
        });

        // doing snapshot after all rendering is done
        const onSnapShot = () => {
            const lastEffect = getLastApplyEffect();
            if (lastEffect) {
                const { renderTexture } = lastEffect;
                this.game.snapshot.setCache(this.snapshotConfig.snapshotKey, renderTexture);
                if (this.snapshotConfig.destroyAfterSnapshot) {
                    this.node.destroy();
                } else {
                    this._effects.forEach((effect) => {
                        effect.enabled = false;
                    });
                }
            }
        };
        director.once(Director.EVENT_AFTER_DRAW, onSnapShot, this);
    }
}
